import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import { toResult } from "#/modules/util.auth/api-errors";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { adminMiddleware } from "#/modules/util.auth/middleware.admin";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { client } from "#/modules/util.fetch-client/fetch-client.server";

/** PUT /user/system-notification-seen — the user closed the popup; it stays away until a new one. */
export const markSystemNotificationSeen = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ systemNotificationId: z.string().min(1) }))
  .handler(async ({ data }) => {
    // Fire-and-forget like the release notes: at worst the popup shows once more.
    toResult(await client.PUT("/user/system-notification-seen", { body: data }));
    return null;
  });

/** GET /admin/system-notification — the active notification, if any. */
export const getAdminSystemNotification = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .handler(async () => {
    const result = toResult(await client.GET("/admin/system-notification"));
    if (Result.isError(result)) throw result.error;
    return { active: result.value.active ?? null };
  });

/** PUT /admin/system-notification — replaces the active notification. */
export const setAdminSystemNotification = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ slug: z.string().min(1), endsAt: z.string().min(1) }))
  .handler(async ({ data }) => {
    const result = toResult(await client.PUT("/admin/system-notification", { body: data }));
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

/** DELETE /admin/system-notification — ends the active notification now. */
export const deleteAdminSystemNotification = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .handler(async () => {
    const result = toResult(await client.DELETE("/admin/system-notification"));
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });
